import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bell, BellRing, X, Check, Calendar, Building2, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const genreLabels = {
  opera: 'Ópera',
  ballet: 'Ballet',
  concierto: 'Concierto',
  teatro: 'Teatro',
  musical: 'Musical',
  cine: 'Cine'
};

export const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [preferences, setPreferences] = useState({ genres: [], enabled: true });
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  
  const userIdRef = useRef(null);

  // Get or create user ID from localStorage - only once
  const getUserId = () => {
    if (userIdRef.current) return userIdRef.current;
    
    let userId = localStorage.getItem('notificationUserId');
    if (!userId) {
      userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('notificationUserId', userId);
    }
    userIdRef.current = userId;
    return userId;
  };

  // Initial load - runs only once
  useEffect(() => {
    if (initialized) return;
    
    const init = async () => {
      const userId = getUserId();
      
      try {
        // Fetch genres
        const genresRes = await axios.get(`${API}/genres`);
        setGenres(genresRes.data);
      } catch (err) {
        console.error('Error fetching genres:', err);
      }
      
      try {
        // Fetch preferences
        const prefsRes = await axios.get(`${API}/notifications/preferences/${userId}`);
        const prefs = prefsRes.data;
        setPreferences(prefs);
        
        // If user has preferences with genres, fetch notifications
        if (prefs.enabled && prefs.genres && prefs.genres.length > 0) {
          try {
            const notifRes = await axios.get(`${API}/notifications/upcoming/${userId}?days=30`);
            const newNotifications = notifRes.data.notifications || [];
            setNotifications(newNotifications);
            
            const lastCount = parseInt(localStorage.getItem('lastNotificationCount') || '0');
            if (newNotifications.length > lastCount) {
              setHasNewNotifications(true);
            }
          } catch (err) {
            console.error('Error fetching notifications:', err);
          }
        }
      } catch (err) {
        // No preferences found - this is normal for new users
        setPreferences({ genres: [], enabled: true });
      }
      
      setInitialized(true);
    };
    
    init();
  }, [initialized]);

  const savePreferences = async (newPrefs) => {
    const userId = getUserId();
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API}/notifications/preferences`, {
        user_id: userId,
        genres: newPrefs.genres,
        enabled: newPrefs.enabled
      });
      setPreferences(newPrefs);
      
      // Fetch notifications with new preferences
      if (newPrefs.enabled && newPrefs.genres.length > 0) {
        try {
          const notifRes = await axios.get(`${API}/notifications/upcoming/${userId}?days=30`);
          setNotifications(notifRes.data.notifications || []);
        } catch (err) {
          console.error('Error fetching notifications:', err);
        }
      } else {
        setNotifications([]);
      }
    } catch (err) {
      console.error('Error saving preferences:', err);
      setError('Error al guardar preferencias');
    } finally {
      setLoading(false);
    }
  };

  const toggleGenre = (genreValue) => {
    const newGenres = preferences.genres.includes(genreValue)
      ? preferences.genres.filter(g => g !== genreValue)
      : [...preferences.genres, genreValue];
    savePreferences({ ...preferences, genres: newGenres });
  };

  const toggleEnabled = () => {
    savePreferences({ ...preferences, enabled: !preferences.enabled });
  };

  const handleOpen = () => {
    setIsOpen(true);
    setHasNewNotifications(false);
    localStorage.setItem('lastNotificationCount', notifications.length.toString());
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="relative">
      {/* Bell Icon */}
      <button
        onClick={handleOpen}
        className="relative p-2 text-white/70 hover:text-white transition-colors"
        data-testid="notification-bell"
      >
        {hasNewNotifications ? (
          <BellRing size={20} className="text-[var(--primary)]" />
        ) : (
          <Bell size={20} />
        )}
        {hasNewNotifications && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-[var(--primary)] rounded-full animate-pulse" />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-80 bg-[#1a1a1a] border border-white/10 shadow-2xl z-50">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="text-white font-medium">
                {showSettings ? 'Configuración' : 'Notificaciones'}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="text-white/50 hover:text-white text-xs"
                >
                  {showSettings ? 'Ver alertas' : 'Configurar'}
                </button>
                <button onClick={() => setIsOpen(false)} className="text-white/50 hover:text-white">
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Settings View */}
            {showSettings ? (
              <div className="p-4 max-h-[400px] overflow-y-auto">
                {error && (
                  <div className="mb-4 p-3 bg-red-950/50 border border-red-500/30 flex items-center gap-2">
                    <AlertCircle size={16} className="text-red-400" />
                    <span className="text-red-400 text-sm">{error}</span>
                  </div>
                )}
                
                <div className="mb-4">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-white text-sm">Activar notificaciones</span>
                    <div 
                      className={`w-12 h-6 rounded-full p-1 transition-colors ${preferences.enabled ? 'bg-[var(--primary)]' : 'bg-white/20'}`}
                      onClick={toggleEnabled}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${preferences.enabled ? 'translate-x-6' : 'translate-x-0'}`} />
                    </div>
                  </label>
                </div>

                <p className="text-white/50 text-xs mb-3">Selecciona los géneros que te interesan:</p>
                
                <div className="space-y-2">
                  {genres.map(genre => (
                    <button
                      key={genre.id}
                      onClick={() => toggleGenre(genre.value)}
                      disabled={loading}
                      className={`w-full p-3 text-left text-sm transition-all flex items-center justify-between ${
                        preferences.genres.includes(genre.value)
                          ? 'bg-[var(--primary)]/20 border border-[var(--primary)]/50 text-white'
                          : 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10'
                      } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span>{genre.label}</span>
                      {preferences.genres.includes(genre.value) && (
                        <Check size={16} className="text-[var(--primary)]" />
                      )}
                    </button>
                  ))}
                </div>

                {preferences.genres.length === 0 && preferences.enabled && (
                  <p className="text-white/40 text-xs text-center mt-4">
                    Selecciona al menos un género para recibir notificaciones
                  </p>
                )}
                
                {preferences.genres.length > 0 && (
                  <p className="text-[var(--primary)]/70 text-xs text-center mt-4">
                    ✓ Recibirás alertas de {preferences.genres.length} género(s) seleccionado(s)
                  </p>
                )}
              </div>
            ) : (
              /* Notifications List */
              <div className="max-h-[400px] overflow-y-auto">
                {!preferences.enabled ? (
                  <div className="p-8 text-center">
                    <Bell size={32} className="mx-auto mb-3 text-white/20" />
                    <p className="text-white/50 text-sm">Notificaciones desactivadas</p>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="text-[var(--primary)] text-sm mt-2 hover:underline"
                    >
                      Activar
                    </button>
                  </div>
                ) : preferences.genres.length === 0 ? (
                  <div className="p-8 text-center">
                    <Bell size={32} className="mx-auto mb-3 text-white/20" />
                    <p className="text-white/50 text-sm">Configura tus géneros favoritos</p>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="text-[var(--primary)] text-sm mt-2 hover:underline"
                    >
                      Configurar
                    </button>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="p-8 text-center">
                    <Bell size={32} className="mx-auto mb-3 text-white/20" />
                    <p className="text-white/50 text-sm">No hay eventos próximos de tus géneros favoritos</p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {notifications.map(notif => (
                      <Link
                        key={notif.id}
                        to={`/evento/${notif.id}`}
                        onClick={() => setIsOpen(false)}
                        className="flex gap-3 p-4 hover:bg-white/5 transition-colors"
                      >
                        {notif.image_url && (
                          <img
                            src={notif.image_url}
                            alt={notif.title}
                            className="w-16 h-16 object-cover"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">{notif.title}</p>
                          <div className="flex items-center gap-2 mt-1 text-white/50 text-xs">
                            <Calendar size={12} />
                            <span>{formatDate(notif.date)}</span>
                          </div>
                          {notif.venue && (
                            <div className="flex items-center gap-2 mt-1 text-white/50 text-xs">
                              <Building2 size={12} />
                              <span className="truncate">{notif.venue}</span>
                            </div>
                          )}
                          <span className="inline-block mt-2 px-2 py-0.5 bg-[var(--primary)]/20 text-[var(--primary)] text-xs">
                            {genreLabels[notif.genre] || notif.genre}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
